import { useEffect, useState } from "react";
import { Typography, Button, Card, CardContent, TableRow, TableCell, Checkbox, setRef, LinearProgress } from "@mui/material";
import MUIDataTable from "mui-datatables";
import {
    useUserContext,
} from "../../../contexts/ProviderProvider.jsx";
import { getEquipmentManagerConfig } from "../../../API/equipmentManager";
import useNotification from "../../../hooks/useNotification.jsx";
import TrueFalseCheckbox from "../../common/TrueFalseCheckbox.jsx";
import { ConfigDetailedView } from "./ConfigDetailedView.jsx";
import { convertSortedIndexNumber } from "../../../utilities/utils.js";

const ConfigureEquipmentManagerTable = ({ loading, setLoading }) => {
    const user = useUserContext();
    const [emConfigurations, setEMConfigurations] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const [open, setOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState([])
    const [selectedMetadataId, setSelectedMetadataId] = useState(-1)
    const [selectedConfigId, setSelectedConfigId] = useState(-1)
    const [selectedAction, setSelectedAction] = useState("")
    const [selectedSku, setSelectedSku] = useState("")
    const [selectedSkuType, setSelectedSkuType] = useState("")
    const [selectedUniversity, setSelectedUniversity] = useState("")
    const [similarConfig, setSimilarConfig] = useState({})

    const notify = useNotification();
    const handleClose = (() => {
        setSelectedAction("")
        setSelectedMetadataId(-1)
        setSelectedConfigId(-1)
        setSelectedRow([])
        setSimilarConfig({});
        setOpen(false);
        setSelectedSku("")
        setSelectedUniversity("")
    })
    useEffect(() => {
        setLoading(true)
        getEquipmentManagerConfig(user)
            .then((response) => {
                setLoading(false)
                // const responseData = decompressData(response);
                const responseData = response.data;
                setEMConfigurations(responseData.obj.missingConfigRecords);
                // const responseData = response.obj
                // setEMConfigurations(responseData.missingConfigRecords);
            })
            .catch((error) => {
                setLoading(false)
                console.error(error);
                notify("Failed to get equipment manager configurations");
            })

    }, [refresh, user]);
    const configOptions = {
        front_icon_general_asset_id: {
            label: "Front Asset Link",
            type: "ImageText",
            required: false,
            name: "front_icon_general_asset_id",
            position: 1,
        },
        front_text_font_link_general_asset_id: {
            label: "Front Asset Font Name",
            type: "Text",
            required: false,
            name: "front_text_font_link_general_asset_id",
            position: 2,
        },
        front_has_sport_text: {
            label: "Front Has Sport Text",
            type: "Checkbox",
            required: false,
            name: "front_has_sport_text",
            position: 3,
        },
        front_text_font_color: {
            label: "Front Asset Text Font Color",
            type: "Color",
            required: false,
            name: "front_text_font_color",
            position: 3,
        },
        front_verified: {
            label: "Front Asset Verified",
            type: "Boolean",
            required: false,
            name: "front_verified",
            position: 4,
        },
        back_text_font_color: {
            label: "Back Asset Text Font Color",
            type: "Color",
            required: false,
            name: "back_text_font_color",
            position: 5,
        },
        back_jersey_number_font_color: {
            label: "Back Asset Jersey Number Font Color",
            type: "Color",
            required: false,
            name: "back_jersey_number_font_color",
            position: 6,
        },
        back_number_outline_color: {
            label: "Back Asset Jersey Number Outline Color",
            type: "Color",
            required: false,
            name: "back_number_outline_color",
            position: 7,
        },
        back_icon_general_asset_id: {
            label: "Back Asset Icon Link",
            type: "ImageText",
            required: false,
            name: "back_icon_general_asset_id",
            position: 8,
        },
        back_text_font_general_asset_id: {
            label: "Back Asset Last Name Font",
            type: "Text",
            required: false,
            name: "back_text_font_general_asset_id",
            position: 9,
        },
        back_jersey_number_font_general_asset_id: {
            label: "Back Asset Jersey Number Font",
            type: "CompositeFontText",
            required: false,
            name: "back_jersey_number_font_general_asset_id",
            position: 10,
        },
        back_verified: {
            label: "Back Asset Verified",
            type: "Boolean",
            required: false,
            name: "back_verified",
            position: 11,
        },
        mockup_template_general_asset_id: {
            label: "Template File Link",
            type: "ImageText",
            required: true,
            name: "mockup_template_general_asset_id",
            position: 12,
        },
        mockup_verified: {
            label: "Mockup Verified",
            type: "Boolean",
            required: false,
            name: "mockup_verified",
            position: 13,
        },
        has_back: {
            label: "Has Back",
            type: "Boolean",
            required: false,
            name: "has_back",
            position: 14,
        },
    };

    var buildAssetRow = (row, metadataId, sku_name, university, sku_type) => {

        setSelectedSku(sku_name);
        setSelectedSkuType(sku_type);
        setSelectedUniversity(university);
        setSelectedMetadataId(metadataId);

        if (row.length === 0) {
            // If no config, initialize empty fields
            const defaultFields = Object.values(configOptions).map(opt => ({
                name: opt.name,
                label: opt.label,
                type: opt.type,
                required: opt.required,
            }));
            setSelectedRow(defaultFields);
            setSelectedConfigId(-1); // or null
        } else {
            const configRow = row[0];
            setSelectedConfigId(configRow.equipment_manager_config_id);

            const tempSelectedRow = [];

            for (const key in configRow) {
                if (key === "equipment_manager_config_id" || key === "metadata_id") continue;

                if (configOptions[key]) {
                    const fieldMeta = configOptions[key];
                    tempSelectedRow.push({
                        name: fieldMeta.name,
                        label: fieldMeta.label,
                        type: fieldMeta.type,
                        required: fieldMeta.required,
                        value: configRow[key] == null ? "" : configRow[key],
                        position: fieldMeta.position || 0,
                    });
                }
            }

            // Sort fields based on position
            tempSelectedRow.sort((a, b) => a.position - b.position);

            setSelectedRow(tempSelectedRow);
        }
    };



    const options = {

        rowsPerPage: window?.rowsPerPage || 100,
        enableNestedDataAccess: ".",
        expandableRows: true,
        isRowExpandable: (dataIndex) => {
            return emConfigurations[dataIndex]["EquipmentManagerConfigs"].length != 0;
        },
        expandableRowsHeader: false,
        selectableRows: "none",
        renderExpandableRow: (rowData, rowMeta) => {
            return (
                <TableRow>
                    <TableCell colSpan={9} >
                        <Card>
                            <CardContent >
                                <Typography id="modal-modal-title" variant="h5" component="h2">
                                    Config Detailed view
                                </Typography>

                                {Object.keys(emConfigurations[rowMeta.dataIndex]["EquipmentManagerConfigs"][0]).map((key, index) => {

                                    return (
                                        <Typography
                                            key={index}
                                            id={`modal-modal-description-${index}`}
                                            sx={{ mt: 2 }}
                                        >
                                            <span style={{ color: "#868686" }}>
                                                {key.toUpperCase().replace("_", " ")}:-
                                            </span>{" "}
                                            {key.toUpperCase().includes("VERIFIED") ?
                                                emConfigurations[rowMeta.dataIndex]["EquipmentManagerConfigs"][0][key] ? "TRUE" : "FALSE"
                                                : emConfigurations[rowMeta.dataIndex]["EquipmentManagerConfigs"][0][key]}

                                        </Typography>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    </TableCell>
                </TableRow >
            );
        },

    };

    const columns = [
        {
            label: "Product Count",
            name: "product_count",
        },
        {
            label: "Sku Name",
            name: "sku_name",
        },
        {
            label: "University",
            name: "university",
        },
        {
            label: "Related Athlete",
            name: "athlete_id",
            options: {
                customBodyRender: (value) => {
                    return <div >{value === null || value === "" ? "ALL" : value}</div>
                }
            }
        },
        {
            label: "Sport",
            name: "sport",
            options: {
                customBodyRender: (value) => {
                    return <div >{value === null || value === "" ? "ALL" : value}</div>;
                }
            }
        },
        {
            label: "Approval Status",
            name: "approval_status",
            options: {
                customFilterListOptions: {
                    render: (v) => (v == "Approved" ? "TRUE" : "FALSE"),
                },
                filterOptions: {
                    renderValue: (v) => (v == "Approved" ? "TRUE" : "FALSE"),
                },
                customBodyRender: (value, tableMeta) => {
                    return <TrueFalseCheckbox checked={value == "Approved"} disabled={true} />
                },
            }
        },
        {
            label: "Jersey number",
            name: "jersey_number",
            options: {
                customBodyRender: (value) => {
                    return <div >{value === null || value === "" ? "ALL" : value}</div>
                }
            }
        },
        {
            label: "Gender",
            name: "gender",
            options: {
                customBodyRender: (value) => {
                    return <div >{value === null || value === "" ? "ALL" : value}</div>
                }
            }
        },
        {
            label: "Has Configuration",
            name: "has_configuration",
            filter: true,
            customFilterListOptions: {
                render: (v) => (v === "TRUE" ? "MISSING CONFIGURATION" : "HAS CONFIGURATION"),
            },
            filterOptions: {
                renderValue: (v) => (v === "TRUE" ? "MISSING CONFIGURATION" : "HAS CONFIGURATION"),
            },
            options: {
                customBodyRender: (value, tableMeta) => {
                    return emConfigurations[convertSortedIndexNumber(tableMeta)]["EquipmentManagerConfigs"].length != 0 ? "TRUE" : "FALSE";
                }
            }
        },
        {
            label: "Configure",
            name: "configure",

            options: {
                customBodyRender: (value, tableMeta) => {
                    const hasConfig = emConfigurations[convertSortedIndexNumber(tableMeta)]["EquipmentManagerConfigs"].length != 0;
                    return (
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 6, bgcolor: hasConfig ? "warning.light" : "success.main" }}
                            onClick={(a) => {
                                setSimilarConfig(emConfigurations[convertSortedIndexNumber(tableMeta)].similar_config);
                                setSelectedAction(hasConfig ? "Update" : "Add");
                                buildAssetRow(emConfigurations[convertSortedIndexNumber(tableMeta)].EquipmentManagerConfigs, emConfigurations[convertSortedIndexNumber(tableMeta)].product_metadata_id, emConfigurations[convertSortedIndexNumber(tableMeta)].sku_name, emConfigurations[convertSortedIndexNumber(tableMeta)].university, emConfigurations[convertSortedIndexNumber(tableMeta)].type);
                                setOpen(true);
                            }
                            }
                        >
                            {hasConfig ? "Modify Config" : "Add Config"}
                        </Button >
                    );
                }
            },
        },
    ];

    return (

        <div style={{ width: "100%", paddingLeft: "20px", paddingRight: "20px" }}>
            <Typography variant="h3">Equipment Manager Configuration</Typography>
            {loading && <LinearProgress />}
            <MUIDataTable
                style={{ width: "90%" }}
                data={emConfigurations}
                columns={columns}
                options={options}
            />
            <ConfigDetailedView
                rows={selectedRow}
                open={open}
                handleClose={handleClose}
                metadataId={selectedMetadataId}
                configId={selectedConfigId}
                action={selectedAction}
                refresh={refresh}
                setRefresh={setRefresh}
                selectedSku={selectedSku}
                selectedUniversity={selectedUniversity}
                similarConfig={similarConfig}
                selectedSkuType={selectedSkuType} />


        </div>
    );
};

export default ConfigureEquipmentManagerTable;
